import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Img from "@insite/mobius/Img";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS } from "@tCentro/theme/design";
import parse from "html-react-parser";
import React, { FC } from "react";
import { css } from "styled-components";

const enum fields {
    image = "image",
    heading = "heading",
    description = "description",
    imageOrder = "imageOrder",
    infoOrder = "infoOrder",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.image]: string;
        [fields.heading]: string;
        [fields.description]: string;
        [fields.imageOrder]: "1" | "2";
        [fields.infoOrder]: "1" | "2";
    };
}

export interface CENSectionImageTextStyles {
    container?: GridContainerProps;
    imageGridItem?: GridItemProps;
    descriptionGridItem?: GridItemProps;
    heading?: InjectableCss;
    description?: InjectableCss;
    quoteImage?: InjectableCss;
}

export const CENsectionImageTextStyles: CENSectionImageTextStyles = {
    container: {
        css: css`
            margin-bottom: 104px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            margin-bottom: 56px;
                        `,
                        css`
                            margin-bottom: 56px;
                        `,
                        css`
                            margin-bottom: 76px;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
            > div[class^="GridOffset"] {
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                display: block;
                                margin-bottom: 56px;
                            `,
                            css`
                                display: block;
                                margin-bottom: 56px;
                            `,
                            css`
                                display: block;
                                margin-bottom: 76px;
                            `,
                            null,
                            null,
                        ],
                        "max",
                    )}
            }
        `,
    },
    imageGridItem: {
        width: [12, 12, 12, 4, 4],
    },
    descriptionGridItem: {
        width: [12, 12, 12, 8, 8],
        css: css`
            flex-direction: column;
        `,
    },
    heading: {
        css: css`
            margin-bottom: 16px !important;
        `,
    },
    description: {
        css: css`
            line-height: 28px !important;
            color: ${getProp("theme.typography.p.color")};
        `,
    },
    quoteImage: {
        css: css`
            width: 100%;
        `,
    },
};

const styles = CENsectionImageTextStyles;
type Props = WidgetProps;

export const CENSectionImageText: FC<Props> = ({ fields }) => {
    const imageOrderCss = {
        css: css`
            order: ${fields.imageOrder || "2"};
        `,
    };
    const infoOrderCss = {
        css: css`
            order: ${fields.infoOrder || "2"};
            flex-direction: column;
        `,
    };
    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.imageGridItem} {...imageOrderCss}>
                <Img src={fields.image} altText={fields.heading} loading="lazy" {...styles.quoteImage} />
            </GridItem>
            <GridItem {...styles.descriptionGridItem} {...infoOrderCss}>
                <Typography variant="h4" {...styles.heading}>
                    {fields.heading}
                </Typography>
                <Typography {...styles.description}>
                    {parse(transformContentHtml(fields.description), parserOptions)}
                </Typography>
            </GridItem>
        </GridContainer>
    );
};

const SectionImageText: WidgetModule = {
    component: CENSectionImageText,
    definition: {
        group: "Common",
        icon: "image-landscape",
        fieldDefinitions: [
            {
                name: fields.imageOrder,
                displayName: "Image Alignment",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "1" },
                    { displayName: "Right", value: "2" },
                ],
                defaultValue: "1",
                fieldType: "Translatable",
            },
            {
                name: fields.infoOrder,
                displayName: "Info Alignment",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "1" },
                    { displayName: "Right", value: "2" },
                ],
                defaultValue: "1",
                fieldType: "Translatable",
            },
            {
                name: fields.image,
                fieldType: "General",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                displayName: "Section Image",
            },
            {
                name: fields.heading,
                displayName: "Heading",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.description,
                displayName: "Description",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
        ],
    },
};

export default SectionImageText;
